/**
 * API client for the in-app cancellation & retention flow.
 *
 * Backend lives in `web/payment/views_cancellation.py` (mounted at
 * `/API/v2/cancellation/...`). The flow is multi-step on the frontend
 * but the backend is intentionally coarse — see `web/payment/CLAUDE.md`
 * for the full design.
 *
 * Mock fallbacks are present (matching the pattern in
 * `controllers/subscription.ts`) so this also works when running locally
 * without a backend (`NEXT_PUBLIC_VERCEL_ENV` undefined).
 */

import axios from "axios";

export type ProductKind = "main" | "api" | "addon";

/** Which addon a `productKind="addon"` call is about. Older TED ignores it and picks the first addon. */
export type AddonKey = "index_rebal" | "options_platform";

/** Index Rebalance Desk is sales-led: TED refuses self-serve changes with `reason: "contact_sales"`. */
export const CONTACT_SALES_EMAIL = "sales@ortex.com";
export const CONTACT_SALES_MESSAGE = `Index Rebalance Desk is managed by your ORTEX account manager. Please contact ${CONTACT_SALES_EMAIL} to change or cancel it.`;
/** The Index Rebalance API product also grants the app; it is an enterprise contract, changed through sales. */
export const CONTACT_SALES_API_MESSAGE = `Your Index Rebalance access comes with your Index Rebalance API subscription, which is managed by your ORTEX account manager. Please contact ${CONTACT_SALES_EMAIL} to change or cancel it.`;

export type IndexRebalTier = "addon" | "desk" | "api" | "legacy";

export interface PricingSummary {
    currency: string;
    last_paid_amount_cents: number;
    next_charge_amount_cents: number;
    interval: string;
    product_name: string;
}

export interface RetentionOfferDto {
    key: string;
    customer_label: string;
    percent_off: number;
    /**
     * How the discount is timed. Drives the user-facing duration copy:
     *   - `once`      → "off your next renewal" (annual subscribers)
     *   - `repeating` → "for the next N months" (monthly subscribers)
     *   - `forever`   → "for as long as your subscription is active"
     *
     * Without this we'd render "for the next N months" for an annual
     * once-off coupon, which is technically wrong (the coupon isn't
     * time-bounded — Stripe applies it to whichever invoice comes next)
     * and misleading to the user.
     */
    duration_type: "once" | "repeating" | "forever";
    /** Number of months the discount applies for. Only meaningful when
     *  duration_type is "repeating"; null otherwise. */
    duration_months: number | null;
    pitch: string;
    /** Computed server-side from pricing — may be null if pricing missing. */
    discounted_amount_cents: number | null;
}

export interface CancellationContext {
    flow_event_id: number;
    product_kind: ProductKind;
    /** True iff the user has a Stripe customer record AND an active subscription
     *  of `product_kind`. The Settings page uses this to gate product-specific
     *  controls — replaces the legacy `/API/v2/user/cancel` endpoint which
     *  could surface either a main or API sub depending on Stripe ordering. */
    stripe_customer: boolean;
    /** ['stripe'], ['google'], ['apple'] etc. — used by Settings to detect
     *  app-store-managed subs. */
    subscription_providers: string[];
    subscription_status: string | null;
    has_pending_cancel: boolean;
    cancel_at: number | null;
    pricing: PricingSummary;
    is_promo_converted: boolean;
    cancel_reasons: { id: number; reason_text: string }[];
    user_types: { id: number; description: string }[];
    recommended_offer: RetentionOfferDto;
    /** Newer TED only (absent on older deploys): the addon this context is about. */
    addon_key?: AddonKey | "";
    /** Newer TED only: Index Rebalance tier of the addon subscription. */
    index_rebal_tier?: IndexRebalTier | null;
    /** Newer TED only: false for Index Rebalance Desk and the API product, which are changed through sales. */
    self_serve_cancel?: boolean;
    /** camelCase aliases, in case this payload is ever camelized like TED's DRF views. */
    addonKey?: AddonKey | "";
    indexRebalTier?: IndexRebalTier | null;
    selfServeCancel?: boolean;
}

/** False only when TED says so explicitly; an older TED (field absent) keeps today's self-serve flow. */
export const isSelfServeCancel = (ctx: Partial<CancellationContext> | null | undefined): boolean => (ctx?.self_serve_cancel ?? ctx?.selfServeCancel) !== false;

/** The Index Rebalance tier TED reported, from either spelling of the field. */
export const indexRebalTierOf = (ctx: Partial<CancellationContext> | null | undefined): IndexRebalTier | null => ctx?.index_rebal_tier ?? ctx?.indexRebalTier ?? null;

/** What a sales-led Index Rebalance customer is told instead of a cancel link. */
export const salesLedMessage = (ctx: Partial<CancellationContext> | null | undefined): string => (indexRebalTierOf(ctx) === "api" ? CONTACT_SALES_API_MESSAGE : CONTACT_SALES_MESSAGE);

/** The settings-page label for the Index Rebalance entitlement. */
export const indexRebalLabel = (ctx: Partial<CancellationContext> | null | undefined): string => {
    const tier = indexRebalTierOf(ctx);
    if (tier === "api") return "Index Rebalance (with your API subscription)";
    if (tier === "desk" || (ctx && !isSelfServeCancel(ctx))) return "Index Rebalance Desk";
    return "Index Rebalance";
};

/** True when a POST was refused because the subscription is sales-led (Index Rebalance Desk or API). */
export const isContactSales = (res: { reason?: string } | null | undefined): boolean => res?.reason === "contact_sales";

export interface OfferPreviewResponse {
    offer: RetentionOfferDto;
    is_promo_converted: boolean;
}

export interface ApplyOfferResponse {
    status: "success" | "error";
    message?: string;
    /** `contact_sales` when the subscription is sales-led (Index Rebalance Desk). */
    reason?: string;
    offer?: RetentionOfferDto;
}

export interface ConfirmCancellationBody {
    product: ProductKind;
    addon_key?: AddonKey;
    flow_event_id: number;
    reason_id: number | null;
    user_type_id: number | null;
    feedback_text: string;
    missing_feature_text?: string;
    competitor_name?: string;
    notify_when_feature_ships?: boolean;
    opt_in_future_offers?: boolean;
}

export interface ConfirmCancellationResponse {
    status: "success" | "error";
    message?: string;
    /** `contact_sales` when the subscription is sales-led (Index Rebalance Desk). */
    reason?: string;
    has_cancelled?: boolean;
    cancel_date?: number;
    product_kind?: ProductKind;
}

const isMockMode = () => !process.env.NEXT_PUBLIC_VERCEL_ENV;

/**
 * Local-dev mock of the newer TED addon fields. `?mock_ir_tier=desk` in the URL previews the
 * sales-led Desk state; `?mock_ir_pending=1` previews a pending cancellation.
 */
const mockAddonFields = (productKind: ProductKind, addonKey?: AddonKey): Partial<CancellationContext> => {
    if (productKind !== "addon" || !addonKey) return {};
    const query = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    const tier = addonKey === "index_rebal" ? (query?.get("mock_ir_tier") === "desk" ? "desk" : "addon") : null;
    const pending = query?.get("mock_ir_pending") === "1";
    return {
        addon_key: addonKey,
        index_rebal_tier: tier,
        self_serve_cancel: tier !== "desk",
        ...(pending ? { has_pending_cancel: true, cancel_at: Date.now() + 1000 * 60 * 60 * 24 * 21 } : {}),
    };
};

/** Quick mock that lets the page render without a backend. */
const mockContext = (productKind: ProductKind, addonKey?: AddonKey): CancellationContext => ({
    flow_event_id: 1,
    product_kind: productKind,
    stripe_customer: true,
    subscription_providers: ["stripe"],
    subscription_status: "active",
    has_pending_cancel: false,
    cancel_at: null,
    pricing: {
        currency: "GBP",
        last_paid_amount_cents: productKind === "api" ? 4900 : 14900,
        next_charge_amount_cents: productKind === "api" ? 4900 : 14900,
        interval: "month",
        product_name: productKind === "api" ? "ORTEX API Advanced" : "ORTEX Advanced",
    },
    is_promo_converted: false,
    cancel_reasons: [
        { id: 1, reason_text: "Too expensive" },
        { id: 2, reason_text: "Not using it enough" },
        { id: 3, reason_text: "Missing a feature or data I need" },
        { id: 4, reason_text: "Found something better" },
        { id: 5, reason_text: "Just exploring / temporary need" },
        { id: 6, reason_text: "Other" },
    ],
    user_types: [
        { id: 1, description: "Active trader" },
        { id: 2, description: "Long-term investor" },
        { id: 3, description: "Researcher / analyst" },
        { id: 4, description: "Hobbyist" },
        { id: 5, description: "Other" },
    ],
    recommended_offer: {
        key: "staying_30_off_3_months",
        customer_label: "Staying: 30% off for 3 months",
        percent_off: 30,
        duration_type: "repeating",
        duration_months: 3,
        pitch: "Take 30% off your next 3 months while you decide.",
        discounted_amount_cents: productKind === "api" ? 3430 : 10430,
    },
    ...mockAddonFields(productKind, addonKey),
});

/**
 * Fetch the cancellation context.
 *
 * Pass `startFlow: true` ONLY from the actual `/cancel/*` pages where the
 * user is entering the flow — that's what tells the backend to create (or
 * resume) a `CancellationFlowEvent`. Passive callers (the Settings page,
 * the global CancellationLossWarning modal) must NOT pass it, otherwise
 * the "Started" funnel KPI on `/sales/cancellations/` gets inflated by
 * every page navigation a paying user makes.
 */
export const getCancellationContext = async (
    productKind: ProductKind,
    options: { startFlow?: boolean; addonKey?: AddonKey } = {}
): Promise<CancellationContext> => {
    if (isMockMode()) return mockContext(productKind, options.addonKey);
    const params = new URLSearchParams({ product: productKind });
    if (options.startFlow) params.set("start_flow", "true");
    if (productKind === "addon" && options.addonKey) params.set("addon_key", options.addonKey);
    const res = await axios.get<CancellationContext>(
        `/API/v2/cancellation/context?${params.toString()}`
    );
    return res.data;
};

/** `{ addon_key }` on addon calls, so TED acts on the right addon subscription. */
const addonBody = (productKind: ProductKind, addonKey?: AddonKey): { addon_key?: AddonKey } => (productKind === "addon" && addonKey ? { addon_key: addonKey } : {});

export const previewOffer = async (params: {
    productKind: ProductKind;
    reasonId: number | null;
    flowEventId: number;
    isSecondAttempt?: boolean;
    addonKey?: AddonKey;
}): Promise<OfferPreviewResponse> => {
    if (isMockMode()) {
        const ctx = mockContext(params.productKind);
        return { offer: ctx.recommended_offer, is_promo_converted: false };
    }
    const res = await axios.post<OfferPreviewResponse>("/API/v2/cancellation/offer-preview", {
        product: params.productKind,
        reason_id: params.reasonId,
        flow_event_id: params.flowEventId,
        is_second_attempt: params.isSecondAttempt || false,
        ...addonBody(params.productKind, params.addonKey),
    });
    return res.data;
};

export const applyRetentionOffer = async (params: {
    productKind: ProductKind;
    offerKey: string;
    flowEventId: number;
    addonKey?: AddonKey;
}): Promise<ApplyOfferResponse> => {
    if (isMockMode()) return { status: "success" };
    const res = await axios.post<ApplyOfferResponse>("/API/v2/cancellation/apply-offer", {
        product: params.productKind,
        offer_key: params.offerKey,
        flow_event_id: params.flowEventId,
        ...addonBody(params.productKind, params.addonKey),
    });
    return res.data;
};

export const recordCrossSellClick = async (params: {
    flowEventId: number;
    target: string;
}): Promise<void> => {
    if (isMockMode()) return;
    try {
        await axios.post("/API/v2/cancellation/cross-sell-clicked", {
            flow_event_id: params.flowEventId,
            target: params.target,
        });
    } catch {
        // analytics-only — swallow
    }
};

export const confirmCancellation = async (
    body: ConfirmCancellationBody
): Promise<ConfirmCancellationResponse> => {
    if (isMockMode()) {
        return {
            status: "success",
            has_cancelled: true,
            cancel_date: Date.now() + 1000 * 60 * 60 * 24 * 28,
            product_kind: body.product,
        };
    }
    const res = await axios.post<ConfirmCancellationResponse>(
        "/API/v2/cancellation/confirm",
        { ...body, ...addonBody(body.product, body.addon_key) }
    );
    return res.data;
};

/**
 * Record the user's intent to switch from annual to monthly billing
 * (alternative-to-cancel save). Backend posts to Slack so support can
 * action the actual subscription change; this just persists the choice
 * and returns the offer block so the UI can confirm the discounted
 * first-month price to the user.
 */
/**
 * Schedule the annual→monthly switch at the user's current period end.
 *
 * Backend wires up a Stripe SubscriptionSchedule (no proration): the
 * existing annual term runs as paid, then the monthly plan with the
 * 30%-off-first-month coupon kicks in at the period boundary.
 *
 * Response includes ``next_charge_date`` (Unix seconds) when the
 * automatic schedule succeeded — the UI can render "Monthly billing
 * starts on <date>" instead of a generic confirmation. On Stripe
 * failure the backend falls back to a Slack alert + manual ops
 * handling and ``next_charge_date`` is absent.
 */
export const switchToMonthly = async (params: {
    flowEventId: number;
}): Promise<{ status: string; offer?: RetentionOfferDto; next_charge_date?: number }> => {
    if (isMockMode()) {
        return {
            status: "success",
            offer: {
                key: "staying_switch_to_monthly_30_off",
                customer_label: "Switch to monthly: 30% off first month",
                percent_off: 30,
                duration_type: "once",
                duration_months: null,
                pitch: "Move to monthly billing and get 30% off your first month.",
                discounted_amount_cents: null,
            },
            next_charge_date: Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 30,
        };
    }
    const res = await axios.post<{ status: string; offer?: RetentionOfferDto; next_charge_date?: number }>(
        "/API/v2/cancellation/switch-to-monthly",
        { flow_event_id: params.flowEventId }
    );
    return res.data;
};

/**
 * Capture the user's price-feedback answer ("what should ORTEX cost?")
 * on the "too expensive" branch. Idempotent — last-write-wins, so a
 * caller can debounce + call repeatedly as the user types without
 * worrying about server-side dedup.
 *
 * All three fields are individually optional; we send whatever the user
 * has provided so far (amount on its own, text on its own, or both).
 */
export const submitPriceFeedback = async (params: {
    flowEventId: number;
    amount?: number | null;
    currency?: string;
    text?: string;
}): Promise<{ status: string }> => {
    if (isMockMode()) return { status: "success" };
    const res = await axios.post<{ status: string }>(
        "/API/v2/cancellation/price-feedback",
        {
            flow_event_id: params.flowEventId,
            amount: params.amount ?? null,
            currency: params.currency ?? "",
            text: params.text ?? "",
        }
    );
    return res.data;
};

export const abortCancellationFlow = async (params: {
    productKind: ProductKind;
    flowEventId: number;
    addonKey?: AddonKey;
}): Promise<{ status: string; message?: string; reason?: string }> => {
    if (isMockMode()) return { status: "success" };
    const res = await axios.post<{ status: string; message?: string }>(
        "/API/v2/cancellation/abort",
        {
            product: params.productKind,
            flow_event_id: params.flowEventId,
            ...addonBody(params.productKind, params.addonKey),
        }
    );
    return res.data;
};

/** Render a price stored in cents into a localised currency string. */
export const formatMoney = (amountCents: number, currencyIso: string): string => {
    const amount = amountCents / 100;
    const upper = (currencyIso || "USD").toUpperCase();
    const symbols: Record<string, string> = {
        GBP: "£",
        EUR: "€",
        USD: "$",
        CAD: "CA$",
        SEK: "kr ",
    };
    const fixed = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
    if (upper === "SEK") return `${fixed} kr`;
    return `${symbols[upper] || upper + " "}${fixed}`;
};
